import * as z from "zod";
import { isPublicPost } from "./blog-visibility";
import type { Locale, Market, SiteContext } from "./site";

export const announcementSchema = z.object({
  showFrom: z.string().date().optional(),
  showUntil: z.string().date(),
  priority: z.number().int().min(0).max(100).default(0),
  text: z.string().trim().min(1).max(140).optional(),
});

export type Announcement = z.infer<typeof announcementSchema>;

export type AnnouncementCandidate = {
  storyId: string;
  locale: Locale;
  slug: string;
  title: string;
  publishedAt: string;
  markets: Market[];
  published: boolean;
  announcement: Announcement;
};

export function announcementWindowError(
  announcement: Announcement,
  publishedAt: string,
): string | null {
  const showFrom = announcement.showFrom ?? publishedAt;
  if (showFrom < publishedAt) return "announcement.showFrom cannot precede publishedAt";
  if (announcement.showUntil < showFrom) {
    return "announcement.showUntil cannot precede announcement.showFrom";
  }
  return null;
}

export function selectAnnouncement(
  candidates: AnnouncementCandidate[],
  site: SiteContext,
  now = new Date(),
): AnnouncementCandidate | null {
  const today = now.toISOString().slice(0, 10);

  return (
    candidates
      .filter((candidate) => candidate.locale === site.locale)
      .filter((candidate) => candidate.markets.includes(site.market))
      .filter((candidate) => isPublicPost(candidate, now))
      .filter((candidate) => {
        const showFrom = candidate.announcement.showFrom ?? candidate.publishedAt;
        return showFrom <= today && candidate.announcement.showUntil >= today;
      })
      .sort(
        (a, b) =>
          b.announcement.priority - a.announcement.priority ||
          b.publishedAt.localeCompare(a.publishedAt) ||
          a.storyId.localeCompare(b.storyId),
      )[0] ?? null
  );
}
